import React, { useMemo, useState } from "react";
import { BookOpen, GraduationCap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ListItemCardCst from "@components/ListItemCardCst";
import AddItemsModalCst, {
  AddItemsConfirmEntry,
  AddItemsTabConfig,
  AddItemsTabFilterDef,
} from "@components/AddItemsModalCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { ACTIVITY_TYPES } from "@pages/Contents/V2/Corporate/Catalog/AddActivity/config/activityTypes";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import { CatalogEntityType } from "@pages/Contents/V2/Corporate/Catalog/types";

export type AddLOKind = Extract<CatalogEntityType, "activity" | "course">;

export interface AddLOSelectedItem {
  id: number;
  title: string;
  type: string;
  kind: AddLOKind;
  entity: Record<string, any>;
}

interface AddLOToPathModalProps {
  open: boolean;
  onClose: () => void;
  excludeIds: number[];
  onConfirm: (added: AddLOSelectedItem[]) => void;
  onCreate?: (tabKey: AddLOKind) => void;
}

const useActivityFilters = (): {
  status: string;
  setStatus: (v: string) => void;
  type: string;
  setType: (v: string) => void;
} => {
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  return { status, setStatus, type, setType };
};

const AddLOToPathModal: React.FC<AddLOToPathModalProps> = ({
  open,
  onClose,
  excludeIds,
  onConfirm,
  onCreate,
}) => {
  const { t } = useTranslation("add-items-modal");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const { t: tActivity } = useTranslation("wizard-activity");
  const { id: corporateId = "" } = useParams();

  const [activityStatus, setActivityStatus] = useState("");
  const [activityType, setActivityType] = useState("");
  const [courseStatus, setCourseStatus] = useState("");

  const activityUrl = corporateId
    ? ServicesURL.getActivities.replace(":idCorporate", corporateId)
    : null;
  const courseUrl = corporateId
    ? ServicesURL.getCoursesAssociation.replace(":idCorporate", corporateId)
    : null;

  const statusOptions = useMemo(
    () => [
      { value: "", label: String(t("filters.status.all") ?? "Tutti") },
      { value: "DRAFT", label: String(t("filters.status.draft") ?? "Bozza") },
      {
        value: "PUBLISHED",
        label: String(t("filters.status.published") ?? "Pubblicato"),
      },
    ],
    [t],
  );

  const activityTypeOptions = useMemo(
    () => [
      { value: "", label: String(t("filters.type.all") ?? "Tutti") },
      ...ACTIVITY_TYPES.map((td) => ({
        value: td.key,
        label: String(tActivity(td.titleI18nKey, { defaultValue: td.key })),
      })),
    ],
    [t, tActivity],
  );

  const useActivityData = (args: {
    page: number;
    pageSize: number;
    search?: string;
  }) =>
    usePaginatedList<Record<string, any>>(activityUrl, {
      page: args.page,
      pageSize: args.pageSize,
      search: args.search,
      fieldFilters: {
        ...(activityStatus ? { status: activityStatus } : {}),
        ...(activityType ? { type: activityType } : {}),
      },
    });

  const useCourseData = (args: {
    page: number;
    pageSize: number;
    search?: string;
  }) =>
    usePaginatedList<Record<string, any>>(courseUrl, {
      page: args.page,
      pageSize: args.pageSize,
      search: args.search,
      fieldFilters: {
        ...(courseStatus ? { status: courseStatus } : {}),
      },
    });

  const renderLORow =
    (kind: AddLOKind) =>
    ({ item }: { item: Record<string, any> }) => {
      const cardProps = mapEntityToCard(item, kind, {
        t: tCatalog,
        tActivity,
        onDetails: () => {
          // TODO: pagina detail v2 non disponibile oggi
          console.info("[add-lo] details — TODO", kind, item.id);
        },
      });
      return <ListItemCardCst {...cardProps} />;
    };

  const tabs: AddItemsTabConfig<Record<string, any>>[] = [
    {
      key: "activity",
      label: String(t("tabs.activities") ?? "Attività"),
      icon: <GraduationCap size={14} />,
      useDataHook: useActivityData,
      renderRow: renderLORow("activity"),
      getId: (it) => Number(it.id),
      rowVariant: "bare",
      useFiltersDefs: (): AddItemsTabFilterDef[] => [
        {
          id: "add-lo-activity-status",
          label: String(t("filters.status.label") ?? "Stato"),
          options: statusOptions,
          value: activityStatus,
          onChange: setActivityStatus,
        },
        {
          id: "add-lo-activity-type",
          label: String(t("filters.type.label") ?? "Tipo"),
          options: activityTypeOptions,
          value: activityType,
          onChange: setActivityType,
        },
      ],
    },
    {
      key: "course",
      label: String(t("tabs.courses") ?? "Corsi"),
      icon: <BookOpen size={14} />,
      useDataHook: useCourseData,
      renderRow: renderLORow("course"),
      getId: (it) => Number(it.id),
      rowVariant: "bare",
      useFiltersDefs: (): AddItemsTabFilterDef[] => [
        {
          id: "add-lo-course-status",
          label: String(t("filters.status.label") ?? "Stato"),
          options: statusOptions,
          value: courseStatus,
          onChange: setCourseStatus,
        },
      ],
    },
  ];

  const handleConfirm = (
    entries: AddItemsConfirmEntry<Record<string, any>>[],
  ) => {
    const added: AddLOSelectedItem[] = entries.map((e) => ({
      id: Number(e.item.id),
      title: e.item.title || e.item.name || `#${e.item.id}`,
      type: e.item.type || e.item.learning_object_type || "",
      kind: e.tabKey as AddLOKind,
      entity: e.item,
    }));
    onConfirm(added);
    onClose();
  };

  const excludeIdsByTab = useMemo(
    () => ({
      activity: excludeIds,
      course: excludeIds,
    }),
    [excludeIds],
  );

  return (
    <AddItemsModalCst<Record<string, any>>
      open={open}
      onClose={onClose}
      title={String(t("title.add-lo") ?? "Aggiungi Elementi")}
      selectionMode="multi"
      tabs={tabs}
      initialTab="activity"
      onConfirm={handleConfirm}
      onCreate={onCreate ? (tab) => onCreate(tab as AddLOKind) : undefined}
      excludeIdsByTab={excludeIdsByTab}
      confirmLabel={String(t("confirm") ?? "Aggiungi")}
      createLabel={String(t("create") ?? "Crea")}
    />
  );
};

export default AddLOToPathModal;
